import { userType, planType, country, homeId } from 'lib/tracking';
import { awaitAllDeferredObjectValues, Deferred } from 'lib/promise';
import { createDNFInvitationPageView } from '@spotify-internal/event-definitions/es5/events/createDNFInvitationPageView';
import { createDNFHomeHubInviteSub } from '@spotify-internal/event-definitions/es5/events/createDNFHomeHubInviteSub';
import { createDNFInvitationPageViewNonAuth } from '@spotify-internal/event-definitions/es5/events/createDNFInvitationPageViewNonAuth';
import { createDNFInvitationPageAction } from '@spotify-internal/event-definitions/es5/events/createDNFInvitationPageAction';
import { createDNFInvitationPageActionNonAuth } from '@spotify-internal/event-definitions/es5/events/createDNFInvitationPageActionNonAuth';
import { createDNFErrorPageView } from '@spotify-internal/event-definitions/es5/events/createDNFErrorPageView';
import { createDNFPageView } from '@spotify-internal/event-definitions/es5/events/createDNFPageView';
import { createDNFPageAction } from '@spotify-internal/event-definitions/es5/events/createDNFPageAction';
import { createDNFFeatureExposure } from '@spotify-internal/event-definitions/es5/events/createDNFFeatureExposure';
import Cookie from 'js-cookie';
import { getLocale } from 'lib/i18n';
import { MemberId } from 'types';
import { ErrorCode } from 'lib/error';

type PageType =
  | 'purchase_confirmation'
  | 'user_has_plan_hopped_error'
  | 'user_has_failed_reverification_error'
  | 'change_account'
  | 'reverification_success'
  | 'reverification_failure'
  | 'reverification_enter_address'
  | 'reverification_address_confirmed'
  | 'reverification_link_expired'
  | 'progress_indicator_one'
  | 'progress_indicator_two'
  | 'address'
  | 'invite'
  | 'member'
  | 'home_hub'
  | 'how_to_invite_members'
  | 'migration_info'
  | 'join_confirmation'
  | 'transition_error'
  | 'address_autocomplete'
  | 'feature_comparison'
  | 'join_landing_page'
  | 'member_remove_page';

const createPageEvent = <A>(pageType: PageType) => ({
  PAGE_VIEW: pageType,

  getAction: (action: A) => ({
    pageType,
    action,
  }),
});

// Following events are not used at the moment:
// const JOIN_LANDING_PAGE = createPageEvent('join_landing_page');

type DeferredOptionalTrackingString = Deferred<string | null | undefined>;

const getDefaultTrackingParams = () => ({
  user_type: userType as DeferredOptionalTrackingString,
  plan_type: planType as DeferredOptionalTrackingString,
  country: country as DeferredOptionalTrackingString,
  home_id: homeId as DeferredOptionalTrackingString,
  sp_t: Cookie.get('sp_t'),
  market: getLocale(),
});

// Raw events

type EventProperties = Record<string, string | null> | null;

const pageView = (pageType: PageType, properties: EventProperties = null) =>
  awaitAllDeferredObjectValues<string | null | undefined | object>({
    ...getDefaultTrackingParams(),
    page_type: pageType,
    ...(properties && { properties }),
  }).then(createDNFPageView);

const pageAction = (
  { pageType, action }: { pageType: PageType; action: string },
  properties: EventProperties = null,
) =>
  awaitAllDeferredObjectValues<string | null | undefined | object>({
    ...getDefaultTrackingParams(),
    page_type: pageType,
    action,
    ...(properties && { properties }),
  }).then(createDNFPageAction);

// Non-generic events

export type ErrorPageType =
  | 'address_mismatch'
  | 'country_mismatch'
  | 'generic'
  | 'invite_has_expired'
  | 'join_own_plan'
  | 'partner_transition'
  | 'plan_is_full'
  | 'user_has_failed_reverification'
  | 'user_has_plan_hopped'
  | 'restricted_country'
  | 'member_removal_page';

export const error = {
  getPageView: (
    errorType: ErrorPageType,
    errorCode: ErrorCode,
    errorMessage: string,
  ) =>
    awaitAllDeferredObjectValues({
      ...getDefaultTrackingParams(),
      error_type: errorType,
      error_code: errorCode,
      error_message: errorMessage,
    }).then(createDNFErrorPageView),
};

type InvitationPageAction = 'join' | 'toc';

const invitationPageActionAuth = (action: InvitationPageAction) =>
  awaitAllDeferredObjectValues({
    ...getDefaultTrackingParams(),
    action,
  }).then(createDNFInvitationPageAction);

const invitationPageActionNonAuth = (action: InvitationPageAction) =>
  awaitAllDeferredObjectValues({
    ...getDefaultTrackingParams(),
    action,
  }).then(createDNFInvitationPageActionNonAuth);

export const joinLandingPageAuth = {
  getPageView: async () =>
    awaitAllDeferredObjectValues(getDefaultTrackingParams()).then(
      createDNFInvitationPageView,
    ),
  getJoin: async () => invitationPageActionAuth('join'),
  getToc: async () => invitationPageActionAuth('toc'),
};

export const joinLandingPageNonAuth = {
  getPageView: async () =>
    awaitAllDeferredObjectValues(getDefaultTrackingParams()).then(
      createDNFInvitationPageViewNonAuth,
    ),
  getJoin: async () => invitationPageActionNonAuth('join'),
  getToc: async () => invitationPageActionNonAuth('toc'),
};

type FeatureName = never;

// @ts-ignore, can be used for future experiments
const featureExposureEvent = (
  featureName: FeatureName,
  properties: EventProperties = null,
) =>
  awaitAllDeferredObjectValues<string | null | undefined | object>({
    ...getDefaultTrackingParams(),
    feature_name: featureName,
    ...(properties && { properties }),
  }).then(createDNFFeatureExposure);

export const featureExposure = {};

// Tracking Events API

// -- purchaseConfirmation

type PurchaseConfirmationPageAction = 'set_address';

const PURCHASE_CONFIRMATION = createPageEvent<PurchaseConfirmationPageAction>(
  'purchase_confirmation',
);

export const purchaseConfirmation = {
  getPageView: () => pageView(PURCHASE_CONFIRMATION.PAGE_VIEW),
  getSetAddress: () =>
    pageAction(PURCHASE_CONFIRMATION.getAction('set_address')),
};

// -- userHasPlanHoppedError

type UserHasPlanHoppedErrorAction = 'see_plans';

const USER_HAS_PLAN_HOPPED = createPageEvent<UserHasPlanHoppedErrorAction>(
  'user_has_plan_hopped_error',
);

export const userHasPlanHoppedError = {
  getSeePlans: () => pageAction(USER_HAS_PLAN_HOPPED.getAction('see_plans')),
};

// -- userHasFailedReverificationError

type UserHasFailedReverificationErrorAction = 'see_plans';

const USER_HAS_FAILED_REVERIFICATION =
  createPageEvent<UserHasFailedReverificationErrorAction>(
    'user_has_failed_reverification_error',
  );

export const userHasFailedReverificationError = {
  getSeePlans: () =>
    pageAction(USER_HAS_FAILED_REVERIFICATION.getAction('see_plans')),
};

// -- changeAccount

type ChangeAccountAction = 'change' | 'confirm';
export type ChangeAccountContext = 'sub_join' | 'reverification';

const CHANGE_ACCOUNT = createPageEvent<ChangeAccountAction>('change_account');

export const changeAccount = {
  getPageView: (context: ChangeAccountContext) =>
    pageView(CHANGE_ACCOUNT.PAGE_VIEW, { context }),
  getChange: (context: ChangeAccountContext) =>
    pageAction(CHANGE_ACCOUNT.getAction('change'), { context }),
  getConfirm: (context: ChangeAccountContext) =>
    pageAction(CHANGE_ACCOUNT.getAction('confirm'), { context }),
};

// -- reverificationEnterAddress

type ReverificationEnterAddressAction = 'save_address' | 'faq';

const REVERIFICATION_ENTER_ADDRESS =
  createPageEvent<ReverificationEnterAddressAction>(
    'reverification_enter_address',
  );

export const reverificationEnterAddress = {
  getPageView: () => pageView(REVERIFICATION_ENTER_ADDRESS.PAGE_VIEW),
  getSaveAddress: () =>
    pageAction(REVERIFICATION_ENTER_ADDRESS.getAction('save_address')),
  getFaq: () => pageAction(REVERIFICATION_ENTER_ADDRESS.getAction('faq')),
};

// -- reverificationAddressConfirmed

type ReverificationAddressConfirmedAction = 'open_spotify';

const REVERIFICATION_ADDRESS_CONFIRMED =
  createPageEvent<ReverificationAddressConfirmedAction>(
    'reverification_address_confirmed',
  );

export const reverificationAddressConfirmed = {
  getPageView: () => pageView(REVERIFICATION_ADDRESS_CONFIRMED.PAGE_VIEW),
  getOpenSpotify: () =>
    pageAction(REVERIFICATION_ADDRESS_CONFIRMED.getAction('open_spotify')),
};

// -- reverificationLinkExpired

type ReverificationLinkExpiredAction = 'open_spotify';

const REVERIFICATION_LINK_EXPIRED =
  createPageEvent<ReverificationLinkExpiredAction>(
    'reverification_link_expired',
  );

export const reverificationLinkExpired = {
  getPageView: () => pageView(REVERIFICATION_LINK_EXPIRED.PAGE_VIEW),
  getOpenSpotify: () =>
    pageAction(REVERIFICATION_LINK_EXPIRED.getAction('open_spotify')),
};

// -- reverificationSuccess

type ReverificationSuccessAction = 'open_spotify';

const REVERIFICATION_SUCCESS = createPageEvent<ReverificationSuccessAction>(
  'reverification_success',
);

export const reverificationSuccess = {
  getPageView: () => pageView(REVERIFICATION_SUCCESS.PAGE_VIEW),
  getOpenSpotify: () =>
    pageAction(REVERIFICATION_SUCCESS.getAction('open_spotify')),
};

// -- reverificationFailure

type ReverificationFailureAction = 'try_again' | 'see_plans' | 'get_help';

const REVERIFICATION_FAILURE = createPageEvent<ReverificationFailureAction>(
  'reverification_failure',
);

export const reverificationFailure = {
  getPageView: () => pageView(REVERIFICATION_FAILURE.PAGE_VIEW),
  getTryAgain: () => pageAction(REVERIFICATION_FAILURE.getAction('try_again')),
  getSeePlans: () => pageAction(REVERIFICATION_FAILURE.getAction('see_plans')),
  getGetHelp: () => pageAction(REVERIFICATION_FAILURE.getAction('get_help')),
};

// -- progressIndicatorOne

type ProgressIndicatorOneAction = 'continue' | 'dismiss';

const PROGRESS_INDICATOR_ONE = createPageEvent<ProgressIndicatorOneAction>(
  'progress_indicator_one',
);

export const progressIndicatorOne = {
  getPageView: () => pageView(PROGRESS_INDICATOR_ONE.PAGE_VIEW),
  getContinue: () => pageAction(PROGRESS_INDICATOR_ONE.getAction('continue')),
  getDismiss: () => pageAction(PROGRESS_INDICATOR_ONE.getAction('dismiss')),
};

// -- progressIndicatorTwo

type ProgressIndicatorTwoAction = 'continue' | 'dismiss';

const PROGRESS_INDICATOR_TWO = createPageEvent<ProgressIndicatorTwoAction>(
  'progress_indicator_two',
);

export const progressIndicatorTwo = {
  getPageView: () => pageView(PROGRESS_INDICATOR_TWO.PAGE_VIEW),
  getContinue: () => pageAction(PROGRESS_INDICATOR_TWO.getAction('continue')),
  getDismiss: () => pageAction(PROGRESS_INDICATOR_TWO.getAction('dismiss')),
};

// -- address

type AddressAction = 'faq' | 'consent' | 'save_address';
export type AddressContext =
  | 'setup'
  | 'sub_join'
  | 'migration'
  | 'change_address'
  | 'reverification';

const ADDRESS = createPageEvent<AddressAction>('address');

export const address = {
  getPageView: (context: AddressContext) =>
    pageView(ADDRESS.PAGE_VIEW, { context }),
  getFaq: (context: AddressContext) =>
    pageAction(ADDRESS.getAction('faq'), { context }),
  getConsent: (context: AddressContext) =>
    pageAction(ADDRESS.getAction('consent'), { context }),
  getSaveAddress: (context: AddressContext) =>
    pageAction(ADDRESS.getAction('save_address'), { context }),
};

// -- addressAutocomplete

type AddressAutocompleteAction =
  | 'select'
  | 'clear'
  | 'faq'
  | 'edit'
  | 'confirm';

const ADDRESS_AUTOCOMPLETE = createPageEvent<AddressAutocompleteAction>(
  'address_autocomplete',
);

export const addressAutocomplete = {
  getSelect: (context: AddressContext) =>
    pageAction(ADDRESS_AUTOCOMPLETE.getAction('select'), { context }),
  getClear: (context: AddressContext) =>
    pageAction(ADDRESS_AUTOCOMPLETE.getAction('clear'), { context }),
  getFaq: (context: AddressContext) =>
    pageAction(ADDRESS_AUTOCOMPLETE.getAction('faq'), { context }),
  getEdit: (context: AddressContext) =>
    pageAction(ADDRESS_AUTOCOMPLETE.getAction('edit'), { context }),
  getConfirm: (context: AddressContext) =>
    pageAction(ADDRESS_AUTOCOMPLETE.getAction('confirm'), { context }),
};

// -- homeHub

type HomeHubAction =
  | 'open_invite'
  | 'change_address'
  | 'remove_sub'
  | 'learn_more_home_mix'
  | 'join_home_mix'
  | 'leave_home_mix'
  | 'confirm_join_home_mix'
  | 'cancel_home_mix_join'
  | 'open_member'
  | 'migration_confirm_address'
  | 'report_account'
  | 'secure_account';

export type HomeHubContext = 'hero' | 'members_card';

const HOME_HUB = createPageEvent<HomeHubAction>('home_hub');

export const homeHub = {
  getPageView: () => pageView(HOME_HUB.PAGE_VIEW),
  getOpenInvite: (context: HomeHubContext | null = null) =>
    pageAction(HOME_HUB.getAction('open_invite'), { context }),
  getChangeAddress: () => pageAction(HOME_HUB.getAction('change_address')),
  getRemoveSub: () => pageAction(HOME_HUB.getAction('remove_sub')),
  getLearnMoreHomeMix: () =>
    pageAction(HOME_HUB.getAction('learn_more_home_mix')),
  getJoinHomeMix: () => pageAction(HOME_HUB.getAction('join_home_mix')),
  getLeaveHomeMix: () => pageAction(HOME_HUB.getAction('leave_home_mix')),
  getConfirmJoinHomeMix: () =>
    pageAction(HOME_HUB.getAction('confirm_join_home_mix')),
  getCancelHomeMixJoin: () =>
    pageAction(HOME_HUB.getAction('cancel_home_mix_join')),
  getOpenMember: () => pageAction(HOME_HUB.getAction('open_member')),
  getMigrationConfirmAddress: () =>
    pageAction(HOME_HUB.getAction('migration_confirm_address')),
  getReportAccount: ({
    memberId,
    memberType,
  }: {
    memberId: string;
    memberType: 'child' | 'regular';
  }) =>
    pageAction(HOME_HUB.getAction('report_account'), {
      member_id: memberId,
      member_type: memberType,
    }),
  getSecureAccount: () => pageAction(HOME_HUB.getAction('secure_account')),
};

// -- joinConfirmation

type JoinConfirmationAction =
  | 'join_home_mix'
  | 'confirm_join_home_mix'
  | 'cancel_home_mix_join'
  | 'open_spotify';

const JOIN_CONFIRMATION =
  createPageEvent<JoinConfirmationAction>('join_confirmation');

export const joinConfirmation = {
  getPageView: () => pageView(JOIN_CONFIRMATION.PAGE_VIEW),
  getJoinHomeMix: () =>
    pageAction(JOIN_CONFIRMATION.getAction('join_home_mix')),
  getConfirmJoinHomeMix: () =>
    pageAction(JOIN_CONFIRMATION.getAction('confirm_join_home_mix')),
  getCancelHomeMixJoin: () =>
    pageAction(JOIN_CONFIRMATION.getAction('cancel_home_mix_join')),
  getOpenSpotify: () => pageAction(JOIN_CONFIRMATION.getAction('open_spotify')),
};

// -- invite

type InviteAction =
  | 'faq'
  | 'download_google_play'
  | 'download_app_store'
  | 'migration_confirm_address'
  | 'open_premium_card'
  | 'close_premium_card'
  | 'open_kids_card'
  | 'close_kids_card'
  | 'abort_native_sharing';

const INVITE = createPageEvent<InviteAction>('invite');

export const invite = {
  getPageView: () => pageView(INVITE.PAGE_VIEW),
  getFaq: () => pageAction(INVITE.getAction('faq')),
  getDownloadGooglePlay: () =>
    pageAction(INVITE.getAction('download_google_play')),
  getDownloadAppStore: () => pageAction(INVITE.getAction('download_app_store')),
  getMigrationConfirmAddress: () =>
    pageAction(INVITE.getAction('migration_confirm_address')),
  getOpenPremiumCard: () => pageAction(INVITE.getAction('open_premium_card')),
  getClosePremiumCard: () => pageAction(INVITE.getAction('close_premium_card')),
  getOpenKidsCard: () => pageAction(INVITE.getAction('open_kids_card')),
  getCloseKidsCard: () => pageAction(INVITE.getAction('close_kids_card')),
  getAbortNativeSharing: () =>
    pageAction(INVITE.getAction('abort_native_sharing')),
};

// -- member

type MemberAction =
  | 'remove_sub'
  | 'allow_explicit_content'
  | 'disallow_explicit_content';

const MEMBER = createPageEvent<MemberAction>('member');

export const member = {
  getPageView: (memberId: MemberId) =>
    pageView(MEMBER.PAGE_VIEW, { member_id: memberId }),
  getRemoveSub: (memberId: MemberId) =>
    pageAction(MEMBER.getAction('remove_sub'), { member_id: memberId }),
  getAllowExplicitContent: (memberId: MemberId) =>
    pageAction(MEMBER.getAction('allow_explicit_content'), {
      member_id: memberId,
    }),
  getDisallowExplicitContent: (memberId: MemberId) =>
    pageAction(MEMBER.getAction('disallow_explicit_content'), {
      member_id: memberId,
    }),
};

// -- migrationInfo

type MigrationInfoAction = 'confirm_address' | 'skip';

const MIGRATION_INFO = createPageEvent<MigrationInfoAction>('migration_info');

export const migrationInfo = {
  getPageView: () => pageView(MIGRATION_INFO.PAGE_VIEW),
  getConfirmAddress: () =>
    pageAction(MIGRATION_INFO.getAction('confirm_address')),
  getSkip: () => pageAction(MIGRATION_INFO.getAction('skip')),
};

// -- inviteSharing

type InviteThroughType =
  | 'copy_link'
  | 'whatsapp'
  | 'messenger'
  | 'email'
  | 'field_click'
  | 'native';

export const inviteSharing = {
  getShareInvite: (inviteThrough: InviteThroughType) =>
    awaitAllDeferredObjectValues({
      ...getDefaultTrackingParams(),
      invite_through: inviteThrough,
    }).then(createDNFHomeHubInviteSub),
};

// -- featureComparison

type FeatureComparisonAction = 'continue' | 'cancel';

const FEATURE_COMPARISON =
  createPageEvent<FeatureComparisonAction>('feature_comparison');

export const featureComparison = {
  getPageView: (currentOffer: string) =>
    pageView(FEATURE_COMPARISON.PAGE_VIEW, {
      current_offer: currentOffer,
    }),
  getContinue: (currentOffer: string) =>
    pageAction(FEATURE_COMPARISON.getAction('continue'), {
      current_offer: currentOffer,
    }),
  getCancel: (currentOffer: string) =>
    pageAction(FEATURE_COMPARISON.getAction('cancel'), {
      current_offer: currentOffer,
    }),
};

// -- transitionError

type TransitionErrorAction = 'manage_account';

const TRANSITION_ERROR =
  createPageEvent<TransitionErrorAction>('transition_error');

export const transitionError = {
  getManageAccount: (currentOffer: string) =>
    pageAction(TRANSITION_ERROR.getAction('manage_account'), {
      current_offer: currentOffer,
    }),
};

// -- howToInviteMembersStandalone

type HowToInviteMembersStandalone = 'how_to_invite_members';

const HOW_TO_INVITE_MEMBERS = createPageEvent<HowToInviteMembersStandalone>(
  'how_to_invite_members',
);

export const howToInviteMembers = {
  getPageView: () => pageView(HOW_TO_INVITE_MEMBERS.PAGE_VIEW),
};

// -- remove member page

type MemberRemoveActions = 'remove_sub' | 'cancel' | 'report' | 'close';

const MEMBER_REMOVE =
  createPageEvent<MemberRemoveActions>('member_remove_page');

export const removeMember = {
  getPageView: () => pageView(MEMBER_REMOVE.PAGE_VIEW),
  getRemoveSub: () => pageAction(MEMBER_REMOVE.getAction('remove_sub')),
  getCancel: () => pageAction(MEMBER_REMOVE.getAction('cancel')),
  getClose: () => pageAction(MEMBER_REMOVE.getAction('close')),
};
